import { FindAllUserResponse, FindAllUsersFilters, UserDTO } from '../app/pages/users/dto';
import { baseApi } from './_baseQuery';


export const usersApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        findAll: build.query<{
            data: { user: UserDTO, avatar: string | null }[]
            total: number,
            page: number,
            prev: boolean,
            next: boolean,
            itemsPerPage: number,
        }, FindAllUsersFilters>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const usersRes = await fetchWithBQ({ url: "/users", params: _arg });
                    if (usersRes.error) return { error: usersRes.error };

                    const usersResponse = usersRes.data as FindAllUserResponse;
                    const users = usersResponse.data;

                    const avatarsRes = await Promise.all(
                        users.map((u) => u.avatarObjectId ? fetchWithBQ("/objects/file/" + u.avatarObjectId) : Promise.resolve({ data: null }))
                    );

                    const usersWithAvatars = users.map((u, i) => ({
                        user: u,
                        avatar: (avatarsRes[i].data as string) ?? null,
                    }));

                    return {
                        data: {
                            data: usersWithAvatars,
                            total: usersResponse.total,
                            page: usersResponse.page,
                            itemsPerPage: usersResponse.perPage,
                            prev: usersResponse.prev,
                            next: usersResponse.next
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch users or avatars',
                            data: error
                        }
                    };
                }
            },
            providesTags: ["Users"],
        }),
        findOne: build.query<{ user: UserDTO, avatar: string | null }, string>({
            async queryFn(id, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const userRes = await fetchWithBQ(`users/${id}`);
                    if (userRes.error) return { error: userRes.error };

                    const user = userRes.data as UserDTO;

                    let avatar: string | null = null;
                    if (user.avatarObjectId) {
                        const avatarRes = await fetchWithBQ("/objects/file/" + user.avatarObjectId);
                        if (!avatarRes.error) {
                            avatar = avatarRes.data as string;
                        }
                    }

                    return {
                        data: {
                            user,
                            avatar
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch user or avatar',
                            data: error
                        }
                    };
                }
            },
            providesTags: (_result, _error, id) => [{ type: 'Users', id }],
        }),
        create: build.mutation<{ id: string }, FormData>({
            query: (body) => ({
                url: `users`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ["Users"],
        }),
        update: build.mutation<void, { id: string; data: FormData }>({
            query: ({ id, data }) => ({
                url: `/users/${id}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: "Users", id },
                "Users"
            ],
        }),
        changeRole: build.mutation<void, { id: string; role: string; teamLeaderId?: string }>({
            query: ({ id, role, teamLeaderId }) => ({
                url: `/users/${id}/role`,
                method: "PUT",
                body: { role, teamLeaderId },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: "Users", id },
                "Users"
            ],
        }),
        remove: build.mutation<{ success: boolean; id: string }, string>({
            query: (id) => ({
                url: `users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [{ type: 'Users', id }, "Users"],
        }),
    }),
})

export const {
    useFindAllQuery,
    useFindOneQuery,
    useCreateMutation,
    useUpdateMutation,
    useChangeRoleMutation,
    useRemoveMutation,
} = usersApi